<template>
  <v-navigation-drawer
    :model-value="modelValue"
    location="right"
    temporary
    width="560"
    class="admin-contract-drawer"
    @update:model-value="$emit('update:modelValue', $event)"
  >
    <div class="admin-contract-drawer__header">
      <div>
        <p>{{ $t("admin.contracts.drawer.eyebrow") }}</p>
        <h2>{{ company?.company_name || $t("admin.contracts.drawer.title") }}</h2>
      </div>
      <v-btn icon="mdi-close" variant="text" :aria-label="$t('app.actions.close')" @click="close" />
    </div>

    <v-progress-linear v-if="loading" indeterminate color="primary" />
    <div v-else class="admin-contract-drawer__body">
      <v-alert v-if="errorMessage" type="error" class="mb-4">
        {{ errorMessage }}
      </v-alert>
      <v-alert v-if="successMessage" type="success" class="mb-4">
        {{ successMessage }}
      </v-alert>

      <v-select
        v-model="form.status"
        :items="statusItems"
        item-title="title"
        item-value="value"
        :label="$t('admin.contracts.fields.status')"
      />
      <v-text-field
        v-model="form.contractNumber"
        :label="$t('admin.contracts.fields.number')"
        maxlength="120"
        clearable
      />

      <div class="admin-contract-drawer__dates">
        <v-text-field
          v-model="form.signedAt"
          type="date"
          :label="$t('admin.contracts.fields.signedAt')"
          :disabled="form.status !== 'signed'"
          :hint="signedAtHint"
          persistent-hint
          clearable
        />
        <v-text-field
          v-model="form.trialEndsAt"
          type="date"
          :label="$t('admin.contracts.fields.trialEndsAt')"
          :hint="$t('admin.contracts.fields.trialEndsAtHint')"
          persistent-hint
          clearable
        />
      </div>
      <p v-if="trialStartedAtLabel" class="admin-contract-drawer__hint">
        {{ trialStartedAtLabel }}
      </p>

      <AdminDeletionGateSection
        v-if="company"
        :company="company"
        @changed="handleGateChanged"
        @deleted="handleDeleted"
      />

      <!-- Dovada acceptării pachetului juridic (`DPA §18.6`). Read-only: este o
           evidență, nu o setare. Se afișează întregul istoric, pentru că o
           reacceptare la o versiune nouă nu o anulează pe cea anterioară. -->
      <div class="admin-contract-drawer__section">
        <h3>{{ $t("admin.contracts.legal.title") }}</h3>
        <p>{{ $t("admin.contracts.legal.subtitle") }}</p>

        <p v-if="!legalAcceptances.length" class="admin-contract-drawer__empty">
          {{ $t("admin.contracts.legal.empty") }}
        </p>

        <div v-for="acceptance in legalAcceptances" :key="acceptance.id" class="admin-contract-drawer__acceptance">
          <div class="admin-contract-drawer__acceptance-head">
            <span class="admin-contract-drawer__acceptance-date">{{ formatDateTime(acceptance.accepted_at) }}</span>
            <v-chip size="x-small" :color="mechanismColor(acceptance.acceptance_mechanism)">
              {{ mechanismLabel(acceptance.acceptance_mechanism) }}
            </v-chip>
          </div>
          <dl class="admin-contract-drawer__acceptance-grid">
            <div v-for="row in acceptanceRows(acceptance)" :key="row.key">
              <dt>{{ row.label }}</dt>
              <dd>
                <a v-if="row.href" :href="row.href" target="_blank" rel="noopener">{{ row.value }}</a>
                <span v-else :title="row.title || null">{{ row.value }}</span>
              </dd>
            </div>
          </dl>
        </div>
      </div>

      <div class="admin-contract-drawer__section">
        <h3>{{ $t("admin.contracts.entitlements.title") }}</h3>
        <p>{{ $t("admin.contracts.entitlements.subtitle") }}</p>
        <v-switch
          v-for="item in entitlementItems"
          :key="item.key"
          v-model="form.entitlementKeys"
          :value="item.key"
          :label="item.label"
          color="tertiary"
          hide-details
          inset
          :disabled="form.status !== 'signed'"
        />
      </div>
    </div>

    <template #append>
      <div class="admin-contract-drawer__actions">
        <v-btn variant="text" :disabled="saving" @click="close">{{ $t("app.actions.cancel") }}</v-btn>
        <v-btn color="primary" variant="flat" :loading="saving" :disabled="loading" @click="requestSave">
          {{ $t("app.actions.save") }}
        </v-btn>
      </div>
    </template>
  </v-navigation-drawer>

  <v-dialog v-model="terminationDialogOpen" max-width="520" persistent>
    <v-card>
      <v-card-title>{{ $t("admin.contracts.termination.title") }}</v-card-title>
      <v-card-text>{{ $t("admin.contracts.termination.body") }}</v-card-text>
      <v-card-actions>
        <v-spacer />
        <v-btn variant="text" :disabled="saving" @click="terminationDialogOpen = false">
          {{ $t("app.actions.cancel") }}
        </v-btn>
        <v-btn color="error" variant="flat" :loading="saving" @click="confirmTermination">
          {{ $t("admin.contracts.termination.confirm") }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { summarizeUserAgent } from "@/Utile/userAgentSummary.mjs";
import { buildDpaDocumentUrl } from "@/Utile/legalDocumentUrls.mjs";
import AdminDeletionGateSection from "@/components/admin/contracts/AdminDeletionGateSection.vue";
import { PREMIUM_ENTITLEMENT_KEYS } from "@/Utile/premiumAccess.mjs";
import {
  formatAdminContractDate,
  toAdminContractDateInput,
  updateAdminPlatformContract,
} from "./adminContractsApi.mjs";
import { formatDisplayDateTime } from "@/Utile/displayFormat.mjs";

export default {
  name: "AdminPlatformContractDrawer",
  components: { AdminDeletionGateSection },
  props: {
    modelValue: { type: Boolean, default: false },
    company: { type: Object, default: null },
    loading: { type: Boolean, default: false },
  },
  emits: ["update:modelValue", "saved", "deleted"],
  data() {
    return {
      saving: false,
      errorMessage: "",
      successMessage: "",
      lastSavedCompanyId: null,
      terminationDialogOpen: false,
      form: {
        status: "unsigned",
        contractNumber: "",
        entitlementKeys: [],
        signedAt: "",
        trialEndsAt: "",
      },
    };
  },
  computed: {
    signedAtHint() {
      return this.form.status === "signed"
        ? this.$t("admin.contracts.fields.signedAtHint")
        : this.$t("admin.contracts.fields.signedAtDisabledHint");
    },
    trialStartedAtLabel() {
      if (!this.company?.trial_started_at) return "";
      return this.$t("admin.contracts.fields.trialStartedAt", {
        date: this.formatDate(this.company.trial_started_at),
      });
    },
    statusItems() {
      return ["unsigned", "signed", "terminated"].map((value) => ({
        value,
        title: this.$t(`admin.contracts.statuses.${value}`),
      }));
    },
    legalAcceptances() {
      return Array.isArray(this.company?.legal_acceptances) ? this.company.legal_acceptances : [];
    },
    entitlementItems() {
      return [
        [PREMIUM_ENTITLEMENT_KEYS.AI_WORKSPACE, "aiWorkspace"],
        [PREMIUM_ENTITLEMENT_KEYS.DATA_INTEGRATIONS, "dataIntegrations"],
        [PREMIUM_ENTITLEMENT_KEYS.EMAIL_MARKETING, "emailMarketing"],
        [PREMIUM_ENTITLEMENT_KEYS.WORKFLOWS, "workflows"],
      ].map(([key, labelKey]) => ({ key, label: this.$t(`admin.contracts.entitlements.items.${labelKey}`) }));
    },
  },
  watch: {
    company: { immediate: true, handler: "handleCompanyChange" },
  },
  methods: {
    formatDateTime(value) {
      if (!value) return this.$t("admin.contracts.legal.unknownDate");
      const parsed = new Date(value);
      return Number.isNaN(parsed.getTime())
        ? String(value)
        : formatDisplayDateTime(parsed);
    },
    mechanismLabel(mechanism) {
      const key = String(mechanism || "").startsWith("clickwrap-migration") ? "migration" : "registration";
      return this.$t(`admin.contracts.legal.mechanisms.${key}`);
    },
    mechanismColor(mechanism) {
      return String(mechanism || "").startsWith("clickwrap-migration") ? "warning" : "tertiary";
    },
    // Textul juridic nu este stocat: fiecare versiune trimite la adresa ei
    // canonică imutabilă, de unde poate fi recuperată oricând.
    acceptanceRows(acceptance) {
      const t = (key) => this.$t(`admin.contracts.legal.fields.${key}`);
      const dash = "—";
      const locale = String(acceptance.locale || "ro").toLowerCase();
      const rows = [
        {
          key: "dpa",
          label: t("dpa"),
          value: acceptance.dpa_version || dash,
          href: acceptance.dpa_version
            ? buildDpaDocumentUrl({ version: acceptance.dpa_version, locale })
            : null,
        },
        {
          key: "terms",
          label: t("terms"),
          value: acceptance.terms_version || dash,
          href: acceptance.terms_version
            ? `https://miridar.com/${locale}/legal/terms/v${acceptance.terms_version}/`
            : null,
        },
        {
          key: "privacy",
          label: t("privacy"),
          value: acceptance.privacy_version || dash,
          href: acceptance.privacy_version
            ? `https://miridar.com/${locale}/legal/gdpr/v${acceptance.privacy_version}/`
            : null,
        },
        { key: "acceptedBy", label: t("acceptedBy"), value: acceptance.accepted_by_email || dash },
        { key: "companyName", label: t("companyName"), value: acceptance.company_name || dash },
        { key: "companyIdno", label: t("companyIdno"), value: acceptance.company_idno || dash },
        {
          key: "representative",
          label: t("representative"),
          value: this.$t(acceptance.declared_representative ? "app.labels.yes" : "app.labels.no"),
        },
        {
          key: "marketing",
          label: t("marketing"),
          value: this.$t(acceptance.marketing_consent ? "app.labels.yes" : "app.labels.no"),
        },
        { key: "locale", label: t("locale"), value: locale.toUpperCase() },
        { key: "sourceIp", label: t("sourceIp"), value: acceptance.source_ip || dash },
        {
          key: "device",
          label: t("device"),
          // Rezumatul este pentru citit; șirul brut rămâne dovada și se vede la
          // hover. Un user agent pe care nu îl putem interpreta se afișează ca
          // atare — „necunoscut" ar ascunde exact informația probatorie.
          value: summarizeUserAgent(acceptance.user_agent) || acceptance.user_agent || dash,
          title: acceptance.user_agent || null,
        },
      ];
      return rows;
    },
    handleCompanyChange(company) {
      const preserveFeedback = Boolean(this.successMessage)
        && Number(company?.company_id) === Number(this.lastSavedCompanyId);
      this.hydrateForm({ preserveFeedback });
      this.lastSavedCompanyId = null;
      this.terminationDialogOpen = false;
    },
    hydrateForm({ preserveFeedback = false } = {}) {
      if (!preserveFeedback) {
        this.errorMessage = "";
        this.successMessage = "";
      }
      this.form = {
        status: this.company?.platform_contract_status || "unsigned",
        contractNumber: this.company?.platform_contract_number || "",
        entitlementKeys: Array.isArray(this.company?.entitlement_keys)
          ? [...this.company.entitlement_keys]
          : [],
        signedAt: toAdminContractDateInput(this.company?.platform_contract_signed_at),
        trialEndsAt: toAdminContractDateInput(this.company?.trial_ends_at),
      };
    },
    formatDate: formatAdminContractDate,
    handleGateChanged(updatedCompany) {
      // The gate mutates lifecycle state directly, so the drawer and the list
      // must reflect it without a manual refresh.
      if (updatedCompany) this.$emit("saved", updatedCompany);
    },
    handleDeleted(result) {
      // The company no longer exists, so the drawer has nothing left to show.
      this.$emit("deleted", result);
      this.close();
    },
    close() {
      this.terminationDialogOpen = false;
      this.$emit("update:modelValue", false);
    },
    requestSave() {
      const becomesTerminated = this.form.status === "terminated"
        && this.company?.platform_contract_status !== "terminated";
      if (becomesTerminated) {
        this.terminationDialogOpen = true;
        return;
      }
      return this.save();
    },
    confirmTermination() {
      this.terminationDialogOpen = false;
      return this.save();
    },
    async save() {
      if (!this.company?.company_id) return;
      this.saving = true;
      this.errorMessage = "";
      this.successMessage = "";
      try {
        const { data } = await updateAdminPlatformContract(this.company.company_id, {
          status: this.form.status,
          contract_number: this.form.contractNumber || null,
          entitlement_keys: this.form.status === "signed" ? this.form.entitlementKeys : [],
          // A non-signed contract has no signing date, mirroring the backend rule.
          signed_at: this.form.status === "signed" ? this.form.signedAt || null : null,
          trial_ends_at: this.form.trialEndsAt || null,
        });
        const updatedCompany = data?.company || null;
        this.successMessage = this.$t("admin.contracts.messages.saved");
        this.lastSavedCompanyId = updatedCompany?.company_id || this.company.company_id;
        this.$emit("saved", updatedCompany);
      } catch (error) {
        this.errorMessage = error?.response?.data?.message || this.$t("admin.contracts.errors.save");
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>

<style scoped>
.admin-contract-drawer__header, .admin-contract-drawer__actions { display: flex; align-items: center; justify-content: space-between; gap: 16px; padding: 20px 24px; border-bottom: 1px solid rgba(var(--v-theme-on-surface), 0.08); }
.admin-contract-drawer__header p { margin: 0 0 3px; color: rgb(var(--v-theme-secondary)); font-size: 0.72rem; font-weight: 700; letter-spacing: 0.07em; text-transform: uppercase; }
.admin-contract-drawer__header h2 { margin: 0; color: var(--app-text-strong); font-size: 1.25rem; }
.admin-contract-drawer__body { padding: 24px; }
.admin-contract-drawer__dates { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; }
.admin-contract-drawer__hint { margin: 10px 0 0; color: var(--app-text-default); font-size: 0.78rem; opacity: 0.7; }
.admin-contract-drawer__section p { color: var(--app-text-default); font-size: 0.78rem; opacity: 0.7; }
@media (max-width: 599px) { .admin-contract-drawer__dates { grid-template-columns: 1fr; } }
.admin-contract-drawer__section { padding-top: 20px; border-top: 1px solid rgba(var(--v-theme-on-surface), 0.08); }
.admin-contract-drawer__section h3 { margin: 0; color: var(--app-text-strong); font-size: 1rem; }
.admin-contract-drawer__empty { font-size: 0.82rem; opacity: 0.7; }
.admin-contract-drawer__acceptance { padding: 12px 14px; margin-bottom: 10px; border: 1px solid rgba(var(--v-theme-on-surface), 0.12); border-radius: var(--app-radius-base); }
.admin-contract-drawer__acceptance:last-child { margin-bottom: 0; }
.admin-contract-drawer__acceptance-head { display: flex; align-items: center; justify-content: space-between; gap: 10px; margin-bottom: 8px; }
.admin-contract-drawer__acceptance-date { color: var(--app-text-strong); font-weight: 600; font-size: 0.86rem; }
.admin-contract-drawer__acceptance-grid { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 6px 14px; margin: 0; }
.admin-contract-drawer__acceptance-grid dt { font-size: 0.72rem; opacity: 0.62; }
.admin-contract-drawer__acceptance-grid dd { margin: 0; font-size: 0.82rem; color: var(--app-text-strong); overflow-wrap: anywhere; }
.admin-contract-drawer__acceptance-grid a { color: rgb(var(--v-theme-primary)); font-weight: 600; }
@media (max-width: 520px) { .admin-contract-drawer__acceptance-grid { grid-template-columns: 1fr; } }
.admin-contract-drawer__section p { margin: 5px 0 12px; line-height: 1.45; }
.admin-contract-drawer__actions { justify-content: flex-end; border-top: 1px solid rgba(var(--v-theme-on-surface), 0.08); border-bottom: 0; }
</style>
